/**
 * PATH: apps/quote/src/components/builder/panels/ProductSummaryPanel.tsx
 * FILE_ID: QB-FE-PRODUCT-SUMMARY-PANEL-001
 * PURPOSE: Right-rail panel that renders the Excel "Product Summary"
 *          worksheet — a section × material matrix showing how much
 *          of each material rolls up across each scope section, plus
 *          a grand total at the bottom.
 *
 * MR F (supplier grouping): When the seed blob carries a
 * `suppliers` map (material → supplier), the panel groups material
 * rows under collapsible supplier headers and adds a TOTAL row
 * showing per-section grand totals. Lets estimators see PO
 * commitments per supplier at a glance ("Sika $X, Bravada Group $Y")
 * rather than scanning 14 ungrouped rows. Quotes loaded before this
 * MR have no suppliers map and fall back to an "Uncategorised" group
 * — same visual as the pre-MR-F layout.
 *
 * RELATED: QB-FE-ASSUMPTIONS-PARSER-001 (parser)
 */

import { Package, Users } from 'lucide-react'
import type { QuoteDetail } from '@/lib/quoteApi'
import {
  parseProductSummary,
  type SupplierGroup,
  type ProductSummary,
} from './assumptions-content-parser'

interface ProductSummaryPanelProps {
  quote: QuoteDetail | null
}

function formatQty(v: number | undefined): string {
  if (v === undefined || v === 0) return '—'
  if (Math.abs(v) >= 1000) {
    return new Intl.NumberFormat('en-AU', {
      maximumFractionDigits: 0,
    }).format(v)
  }
  return new Intl.NumberFormat('en-AU', {
    maximumFractionDigits: 2,
  }).format(v)
}

function EmptyState() {
  return (
    <div className="p-6 text-center text-slate-500 dark:text-slate-400">
      <Package className="w-12 h-12 mx-auto mb-3 opacity-40" />
      <p className="font-medium">No product summary data</p>
      <p className="text-sm mt-1">
        Excel "Product Summary" worksheet maps here when the quote was
        imported from the .xlsm.
      </p>
    </div>
  )
}

export default function ProductSummaryPanel({ quote }: ProductSummaryPanelProps) {
  const psum = parseProductSummary(quote?.config)
  if (!psum || psum.materials.length === 0) return <EmptyState />

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Package className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        <h3 className="font-semibold text-slate-900 dark:text-white">
          Product Summary
        </h3>
      </div>

      <p className="text-xs text-slate-500 dark:text-slate-400">
        Material take-off rolled up by section, mirrored from the Excel
        "Product Summary" tab.
      </p>

      {/* Material × section matrix — grouped by supplier when present */}
      <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 dark:bg-slate-800/50">
              <tr className="text-slate-600 dark:text-slate-400">
                <th className="px-3 py-2 text-left font-medium sticky left-0 bg-slate-50 dark:bg-slate-800/50 z-10">
                  Material
                </th>
                <th className="px-3 py-2 text-right font-medium">DPK 4705</th>
                <th className="px-3 py-2 text-right font-medium">DPK 4706</th>
                <th className="px-3 py-2 text-right font-medium bg-emerald-50 dark:bg-emerald-900/20">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {psum.bySupplier.map((group) => (
                <SupplierBlock key={group.supplier} group={group} psum={psum} />
              ))}
              {/* Grand totals — same numbers Excel shows on the bottom
                  row of the Product Summary sheet. Rendered as a sticky
                  emphasis row so estimators reading top-down always end
                  on the project-wide material take-off totals. */}
              <tr className="bg-emerald-50 dark:bg-emerald-900/30 font-semibold">
                <td className="px-3 py-2 text-sm text-emerald-900 dark:text-emerald-100 sticky left-0 bg-emerald-50 dark:bg-emerald-900/30 z-10">
                  Grand Totals
                </td>
                <td className="px-3 py-2 text-right text-sm font-mono text-emerald-800 dark:text-emerald-200">
                  {formatQty(psum.grandTotals.dpk4705)}
                </td>
                <td className="px-3 py-2 text-right text-sm font-mono text-emerald-800 dark:text-emerald-200">
                  {formatQty(psum.grandTotals.dpk4706)}
                </td>
                <td className="px-3 py-2 text-right text-sm font-mono font-bold text-emerald-900 dark:text-emerald-100">
                  {formatQty(psum.grandTotals.both)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-[10px] text-slate-400 dark:text-slate-500 italic">
        Quantities are in the material's native unit (m², m, ea — depending
        on the line). Refer to the source Excel for unit dimensions per
        material.
      </p>
    </div>
  )
}

function SupplierBlock({
  group,
  psum,
}: {
  group: SupplierGroup
  psum: ProductSummary
}) {
  // Supplier header + per-material rows + supplier subtotal. When the
  // suppliers map is absent, we still render ONE group ("Uncategorised")
  // so the layout stays consistent across pre-/post-MR-F quotes.
  const showHeader = group.supplier !== 'Uncategorised'
  return (
    <>
      {showHeader && (
        <tr className="bg-blue-50/60 dark:bg-blue-900/20">
          <td
            colSpan={4}
            className="px-3 py-1.5 text-xs font-semibold text-blue-800 dark:text-blue-300 sticky left-0"
          >
            <div className="flex items-center gap-1.5">
              <Users className="w-3 h-3" />
              {group.supplier}
              <span className="text-blue-600/70 dark:text-blue-400/70 font-normal">
                · {group.materials.length} material(s)
              </span>
            </div>
          </td>
        </tr>
      )}
      {group.materials.map((mat) => {
        const a = psum.totals.dpk4705[mat]
        const b = psum.totals.dpk4706[mat]
        const t = psum.totals.both[mat]
        return (
          <tr
            key={mat}
            className="hover:bg-slate-50 dark:hover:bg-slate-800/30"
          >
            <td className="px-3 py-2 pl-6 text-sm text-slate-900 dark:text-white sticky left-0 bg-white dark:bg-slate-900 z-10 truncate max-w-[200px]">
              {mat}
            </td>
            <td className="px-3 py-2 text-right text-sm font-mono text-slate-700 dark:text-slate-300">
              {formatQty(a)}
            </td>
            <td className="px-3 py-2 text-right text-sm font-mono text-slate-700 dark:text-slate-300">
              {formatQty(b)}
            </td>
            <td className="px-3 py-2 text-right text-sm font-mono font-semibold text-emerald-700 dark:text-emerald-300 bg-emerald-50/40 dark:bg-emerald-900/10">
              {formatQty(t)}
            </td>
          </tr>
        )
      })}
      {showHeader && (
        <tr className="bg-blue-50/30 dark:bg-blue-900/10 text-xs">
          <td className="px-3 py-1.5 pl-6 text-blue-700 dark:text-blue-300 sticky left-0 bg-blue-50/30 dark:bg-blue-900/10">
            Subtotal · {group.supplier}
          </td>
          <td className="px-3 py-1.5 text-right font-mono text-blue-700 dark:text-blue-300">
            {formatQty(group.totalDpk4705)}
          </td>
          <td className="px-3 py-1.5 text-right font-mono text-blue-700 dark:text-blue-300">
            {formatQty(group.totalDpk4706)}
          </td>
          <td className="px-3 py-1.5 text-right font-mono font-semibold text-blue-800 dark:text-blue-200">
            {formatQty(group.totalBoth)}
          </td>
        </tr>
      )}
    </>
  )
}
